/**
 * undercover® — shoppable reels (<uc-reels>, sections/uc-reels.liquid)
 *
 * Tegels: video pas in de DOM (uit <template>) als de tegel in beeld komt, gedempt afspelen vanaf 60% zichtbaar,
 * op mobiel max. één tegelijk (de meest zichtbare), op desktop de zichtbare. Geen autoplay bij reduced motion.
 * Viewer: <dialog> (showModal → focus blijft binnen), verticale scroll-snap = swipe omhoog/omlaag,
 * pijltjes/j/k/PageUp/PageDown, m = geluid, spatie = pauze, Esc/X/omlaag trekken (bovenaan) = sluiten.
 * Shop deze look: varianten via optiewaarden (optienaam-onafhankelijk), window.ucCart.add → drawer.
 * Meten: ucTrack('reel_opened'), ucTrack('reel_add_to_cart').
 */
(() => {
  if (customElements.get('uc-reels')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px)');
  const track = (name, data) => window.ucTrack?.(name, data);

  const getCart = () =>
    window.ucCart
      ? Promise.resolve(window.ucCart)
      : new Promise((resolve) => document.addEventListener('uc:cart-ready', () => resolve(window.ucCart), { once: true }));

  const safePlay = (video) => {
    if (!video) return;
    const promise = video.play();
    if (promise) promise.catch(() => {});
  };

  /** Zet de <video> uit de <template> in de DOM (preload none: laadt pas bij play). */
  const mountVideo = (media) => {
    if (!media) return null;
    let video = media.querySelector('video');
    if (video) return video;
    const template = media.querySelector('template[data-video]');
    if (!template) return null;
    media.append(template.content.cloneNode(true));
    video = media.querySelector('video');
    if (!video) return null;
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = 'none';
    video.removeAttribute('controls');
    video.removeAttribute('autoplay');
    video.setAttribute('tabindex', '-1');
    video.setAttribute('aria-hidden', 'true');
    video.addEventListener('playing', () => media.classList.add('is-playing'));
    video.addEventListener('loadeddata', () => media.classList.add('has-frame'));
    return video;
  };

  /* ------------------------------------------------------------------------
     Shop deze look: één kaart
     ------------------------------------------------------------------------ */
  class ReelProduct {
    constructor(el, owner) {
      this.el = el;
      this.owner = owner;
      this.data = owner.products[el.dataset.handle];
      this.context = el.classList.contains('uc-rp--viewer') ? 'viewer' : 'tile';
      this.groups = [...el.querySelectorAll('[data-rp-option]')];
      this.addButtons = [...el.querySelectorAll('[data-rp-add]')];
      this.quick = el.querySelector('[data-rp-quick]');
      this.hint = el.querySelector('[data-rp-hint]');
      this.img = el.querySelector('[data-rp-img]');
      this.tile = el.closest('[data-reel-tile]');
      this.slide = el.closest('[data-rv-slide]');

      el.addEventListener('change', () => this.update());
      this.addButtons.forEach((button) => button.addEventListener('click', () => this.add(button)));
      this.quick?.addEventListener('click', () => this.onQuick());
      el.querySelector('[data-rp-back]')?.addEventListener('click', () => this.toggle(false));
      el.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.el.classList.contains('is-choosing')) {
          event.stopPropagation();
          event.preventDefault();
          this.toggle(false);
        }
      });
      this.update();
    }

    get single() {
      return this.el.dataset.single === 'true';
    }

    /** Geselecteerde waarde per optie-index (null = nog niet gekozen). Vaste opties (1 waarde) vullen we zelf in. */
    selection() {
      if (!this.data) return [];
      const options = this.data.options.map((_, i) => {
        const values = new Set(this.data.variants.map((v) => v.options[i]));
        return values.size === 1 ? [...values][0] : null;
      });
      for (const group of this.groups) {
        const i = Number(group.dataset.rpOption);
        const input = group.querySelector('input:checked');
        const select = group.querySelector('select');
        options[i] = input ? input.value : select?.value || null;
      }
      return options;
    }

    variant() {
      if (!this.data) return null;
      if (this.single) return this.data.variants[0];
      const selected = this.selection();
      if (selected.some((v) => v === null)) return null;
      return this.data.variants.find((v) => v.options.every((o, i) => o === selected[i])) || null;
    }

    /** Beschikbaarheid per waarde, gegeven de keuzes in de andere groepen. */
    update() {
      if (!this.data) return;
      const selected = this.selection();
      for (const group of this.groups) {
        const i = Number(group.dataset.rpOption);
        const controls = [...group.querySelectorAll('input, option')].filter((c) => c.value !== '');
        for (const control of controls) {
          const possible = this.data.variants.some(
            (v) => v.available && v.options[i] === control.value && v.options.every((o, j) => j === i || selected[j] === null || o === selected[j])
          );
          const exists = this.data.variants.some((v) => v.available && v.options[i] === control.value);
          control.disabled = !exists;
          control.closest('.uc-rp__pill')?.classList.toggle('is-unavailable', !possible);
          if (control.tagName === 'INPUT' && control.checked && !exists) control.checked = false;
        }
        if (group.classList.contains('is-missing') && selected[i] !== null) group.classList.remove('is-missing');
      }

      const variant = this.variant();
      const t = this.owner.strings;
      if (variant) {
        this.el.querySelectorAll('[data-rp-price]').forEach((price) => {
          price.innerHTML = '';
          price.append(variant.price);
          if (variant.compare) {
            const s = document.createElement('s');
            s.textContent = variant.compare;
            price.append(s);
          }
        });
        if (variant.image && this.img) {
          this.img.srcset = '';
          this.img.src = variant.image;
        }
      }
      if (this.hint) this.hint.textContent = variant && !variant.available ? t.unavailable : '';
      for (const button of this.addButtons) {
        const soldOut = (variant && !variant.available) || (this.single && !variant?.available);
        button.disabled = Boolean(soldOut);
        const label = button.querySelector('[data-rp-label]');
        if (label && !button.classList.contains('is-added')) label.textContent = soldOut ? t.soldOut : t.addToCart;
      }
    }

    toggle(open) {
      if (!this.quick || this.single) return;
      this.el.classList.toggle('is-choosing', open);
      this.tile?.classList.toggle('is-choosing', open);
      this.quick.setAttribute('aria-expanded', String(open));
      if (open) {
        this.owner.closeOtherPanels(this);
        const first = this.el.querySelector('[data-rp-panel] input:not(:disabled), [data-rp-panel] select');
        requestAnimationFrame(() => first?.focus({ preventScroll: true }));
      } else if (this.el.contains(document.activeElement)) {
        this.quick.focus({ preventScroll: true });
      }
    }

    onQuick() {
      if (this.single) return this.add(this.quick);
      this.toggle(!this.el.classList.contains('is-choosing'));
    }

    nudge(missingIndex) {
      const t = this.owner.strings;
      const group = this.groups.find((g) => Number(g.dataset.rpOption) === missingIndex);
      if (!group) return;
      group.classList.add('is-missing');
      this.el.classList.remove('is-nudging');
      void this.el.offsetWidth;
      this.el.classList.add('is-nudging');
      if (this.hint) this.hint.textContent = t.chooseFirst.replace('%option%', group.dataset.optionName || '');
      group.querySelector('input:not(:disabled), select')?.focus({ preventScroll: true });
    }

    async add(button) {
      const variant = this.variant();
      if (!variant) {
        const missing = this.selection().findIndex((v) => v === null);
        return this.nudge(missing);
      }
      if (!variant.available || button.getAttribute('aria-busy') === 'true') return;

      const t = this.owner.strings;
      const label = button.querySelector('[data-rp-label]');
      button.setAttribute('aria-busy', 'true');
      if (label) label.textContent = t.adding;

      const holder = this.tile || this.slide;
      const index = Number(holder?.dataset.index || 0);
      try {
        const cart = await getCart();
        await cart.add([{ id: variant.id, quantity: 1 }], { source: 'reels', origin: button });
        track('reel_add_to_cart', {
          index,
          handle: holder?.dataset.handle || '',
          product: this.el.dataset.handle,
          variant_id: variant.id,
          context: this.context,
        });
        button.classList.add('is-added');
        if (label) label.textContent = t.added;
        if (this.hint) this.hint.textContent = '';
        setTimeout(() => {
          button.classList.remove('is-added');
          this.update();
        }, 2400);
        if (this.context === 'tile') {
          this.toggle(false);
          this.quick?.classList.add('is-added');
          setTimeout(() => this.quick?.classList.remove('is-added'), 2000);
        } else {
          // Horizon opent de drawer zodra deze modale dialog dicht is (cart-drawer.js wacht op 'close')
          this.owner.closeViewer({ restoreFocus: true });
        }
      } catch (error) {
        if (label) label.textContent = t.addToCart;
      } finally {
        button.removeAttribute('aria-busy');
      }
    }
  }

  /* ------------------------------------------------------------------------
     Sectie
     ------------------------------------------------------------------------ */
  class UcReels extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-rail]');
      this.tiles = [...this.querySelectorAll('[data-reel-tile]')];
      this.dialog = this.querySelector('[data-viewer]');
      if (!this.rail || !this.tiles.length) return;

      try {
        this.products = JSON.parse(this.querySelector('script[data-reels-products]')?.textContent || '{}');
      } catch (error) {
        this.products = {};
      }
      const label = (key, fallback) => this.dialog?.querySelector(`[data-label-${key}]`)?.getAttribute(`data-label-${key}`) || fallback;
      this.strings = {
        addToCart: this.dataset.tAdd || 'in winkelwagen',
        soldOut: this.dataset.tSoldout || 'uitverkocht',
        adding: this.dataset.tAdding || 'even toevoegen',
        added: this.dataset.tAdded || 'toegevoegd',
        unavailable: this.dataset.tUnavailable || '',
        chooseFirst: this.dataset.tChoose || 'kies eerst je %option%',
        position: this.dataset.tPosition || '%index% / %count%',
        soundOn: label('on', 'geluid aan'),
        soundOff: label('off', 'geluid uit'),
        play: label('play', 'afspelen'),
        pause: label('pause', 'pauzeren'),
      };

      this.autoplay = this.dataset.autoplay !== 'false';
      this.ratios = new Map();
      this.cards = [...this.querySelectorAll('[data-uc-rp]')].map((el) => new ReelProduct(el, this));
      this.setupLists();
      this.setupRail();
      this.setupTiles();
      this.setupViewer();

      this.onVisibility = () => this.updateTiles();
      document.addEventListener('visibilitychange', this.onVisibility);
      this.onMotion = () => this.updateTiles();
      reduceMotion.addEventListener?.('change', this.onMotion);
      desktop.addEventListener?.('change', this.onMotion);

      this.onBlockSelect = (event) => {
        const tile = event.target.closest?.('[data-reel-tile]');
        if (!tile || !this.contains(tile)) return;
        this.scrollRailTo(tile);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      this.tileObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.listObserver?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      reduceMotion.removeEventListener?.('change', this.onMotion);
      desktop.removeEventListener?.('change', this.onMotion);
      cancelAnimationFrame(this.raf);
      if (this.dialog?.open) this.dialog.close();
    }

    closeOtherPanels(except) {
      this.cards.forEach((card) => card !== except && card.el.classList.contains('is-choosing') && card.toggle(false));
    }

    /* Rij + pijlen ----------------------------------------------------------- */
    setupRail() {
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      const step = () => Math.max(this.tiles[0].offsetWidth, this.rail.clientWidth * 0.8);
      this.prev?.addEventListener('click', () => this.rail.scrollBy({ left: -step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
      this.next?.addEventListener('click', () => this.rail.scrollBy({ left: step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
      let ticking = false;
      const updateArrows = () => {
        ticking = false;
        const max = this.rail.scrollWidth - this.rail.clientWidth - 2;
        if (this.prev) this.prev.disabled = this.rail.scrollLeft <= 2;
        if (this.next) this.next.disabled = this.rail.scrollLeft >= max;
        const nav = this.prev?.parentElement;
        if (nav) nav.hidden = max <= 2;
      };
      this.resizeObserver = new ResizeObserver(() => updateArrows());
      this.resizeObserver.observe(this.rail);
      this.rail.addEventListener('scroll', () => {
        if (!ticking) requestAnimationFrame(updateArrows);
        ticking = true;
      }, { passive: true });
      updateArrows();
    }

    scrollRailTo(tile) {
      const left = tile.offsetLeft - parseFloat(getComputedStyle(this.rail).scrollPaddingInlineStart || 0);
      this.rail.scrollTo({ left, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    /* Productlijstjes: dots bijwerken ----------------------------------------- */
    setupLists() {
      this.querySelectorAll('[data-rp-list]').forEach((list) => {
        const trackEl = list.querySelector('[data-rp-track]');
        const dots = [...list.querySelectorAll('.uc-rlist__dots span')];
        if (!trackEl || dots.length < 2) return;
        trackEl.addEventListener('scroll', () => {
          const width = trackEl.firstElementChild?.offsetWidth || trackEl.clientWidth;
          const i = Math.round(trackEl.scrollLeft / (width + 8));
          dots.forEach((dot, n) => dot.classList.toggle('is-active', n === i));
        }, { passive: true });
      });
    }

    /* Tegels: lazy video + autoplay ------------------------------------------ */
    setupTiles() {
      this.tiles.forEach((tile) => {
        tile.querySelector('[data-reel-open]')?.addEventListener('click', () => this.openViewer(Number(tile.dataset.index), tile));
      });
      this.tileObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            this.ratios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
          }
          this.updateTiles();
        },
        { threshold: [0, 0.3, 0.6, 0.8, 1] }
      );
      this.tiles.forEach((tile) => this.tileObserver.observe(tile));
    }

    updateTiles() {
      const allowed = this.autoplay && !reduceMotion.matches && !document.hidden && !this.dialog?.open;
      let active = [];
      if (allowed) {
        const visible = this.tiles.filter((tile) => (this.ratios.get(tile) || 0) >= 0.6);
        active = desktop.matches
          ? visible
          : visible.sort((a, b) => (this.ratios.get(b) || 0) - (this.ratios.get(a) || 0) || Number(a.dataset.index) - Number(b.dataset.index)).slice(0, 1);
      }
      for (const tile of this.tiles) {
        const on = active.includes(tile);
        tile.classList.toggle('is-live', on);
        const media = tile.querySelector('[data-reel-media]');
        const video = on ? mountVideo(media) : media.querySelector('video');
        if (!video) continue;
        if (on) safePlay(video);
        else if (!video.paused) video.pause();
      }
    }

    /* Viewer ----------------------------------------------------------------- */
    setupViewer() {
      const dialog = this.dialog;
      if (!dialog) return;
      this.track = dialog.querySelector('[data-rv-track]');
      this.slides = [...dialog.querySelectorAll('[data-rv-slide]')];
      this.segs = [...dialog.querySelectorAll('[data-rv-seg]')];
      this.countEl = dialog.querySelector('[data-rv-count]');
      this.soundBtn = dialog.querySelector('[data-rv-sound]');
      this.playBtn = dialog.querySelector('[data-rv-play]');
      this.prevBtn = dialog.querySelector('[data-rv-prev]');
      this.nextBtn = dialog.querySelector('[data-rv-next]');
      this.muted = true;
      this.paused = false;
      this.current = -1;

      dialog.querySelector('[data-rv-close]')?.addEventListener('click', () => this.closeViewer());
      this.prevBtn?.addEventListener('click', () => this.go(this.current - 1));
      this.nextBtn?.addEventListener('click', () => this.go(this.current + 1));
      this.soundBtn?.addEventListener('click', () => this.setMuted(!this.muted));
      this.playBtn?.addEventListener('click', () => this.setPaused(!this.paused));

      // Tik op het beeld = pauze/afspelen; klik naast het frame (desktop) = sluiten
      dialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-rv-media]')) return this.setPaused(!this.paused);
        if (event.target.matches('[data-rv-slide], .uc-rv__backdrop, .uc-rv__backdrop *')) this.closeViewer();
      });

      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeViewer();
      });
      dialog.addEventListener('close', () => this.onViewerClosed());

      dialog.addEventListener('keydown', (event) => {
        if (event.defaultPrevented || event.altKey || event.metaKey || event.ctrlKey) return;
        const field = event.target.closest('input, select, textarea');
        const key = event.key;
        if (!field && ['ArrowDown', 'PageDown', 'j'].includes(key)) {
          event.preventDefault();
          this.go(this.current + 1);
        } else if (!field && ['ArrowUp', 'PageUp', 'k'].includes(key)) {
          event.preventDefault();
          this.go(this.current - 1);
        } else if (!field && (key === 'ArrowRight' || key === 'ArrowLeft') && !event.target.closest('.uc-rp')) {
          event.preventDefault();
          this.go(this.current + (key === 'ArrowRight' ? 1 : -1));
        } else if (!field && key === 'm') {
          this.setMuted(!this.muted);
        } else if (key === ' ' && !event.target.closest('button, a, input, select')) {
          event.preventDefault();
          this.setPaused(!this.paused);
        }
      });

      let ticking = false;
      this.track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          if (!dialog.open) return;
          const i = Math.round(this.track.scrollTop / this.track.clientHeight);
          if (i !== this.current) this.setActive(i);
        });
      }, { passive: true });

      this.setupPullToClose();
    }

    setupPullToClose() {
      const dialog = this.dialog;
      let startY = null;
      let startX = 0;
      let dy = 0;
      this.track.addEventListener('touchstart', (event) => {
        if (this.track.scrollTop > 2 || event.touches.length > 1 || event.target.closest('.uc-rlist__track')) return (startY = null);
        startY = event.touches[0].clientY;
        startX = event.touches[0].clientX;
        dy = 0;
      }, { passive: true });
      this.track.addEventListener('touchmove', (event) => {
        if (startY === null) return;
        dy = event.touches[0].clientY - startY;
        const dx = Math.abs(event.touches[0].clientX - startX);
        if (dy <= 0 || dx > dy || this.track.scrollTop > 2) {
          if (dialog.classList.contains('is-pulling')) this.resetPull();
          return;
        }
        const pull = Math.min(dy * 0.7, 260);
        dialog.classList.add('is-pulling');
        dialog.style.setProperty('--pull', `${pull}px`);
        dialog.style.setProperty('--pull-n', String(Math.min(pull / 260, 1)));
      }, { passive: true });
      const end = () => {
        if (startY === null) return;
        startY = null;
        if (dialog.classList.contains('is-pulling') && dy > 110) this.closeViewer();
        else this.resetPull();
      };
      this.track.addEventListener('touchend', end);
      this.track.addEventListener('touchcancel', end);
    }

    resetPull() {
      const dialog = this.dialog;
      if (!dialog.classList.contains('is-pulling')) return;
      dialog.classList.add('is-releasing');
      dialog.style.setProperty('--pull', '0px');
      dialog.style.setProperty('--pull-n', '0');
      setTimeout(() => dialog.classList.remove('is-pulling', 'is-releasing'), 320);
    }

    openViewer(index, tile) {
      const dialog = this.dialog;
      if (!dialog || dialog.open) return;
      this.closeOtherPanels(null);
      this.returnTile = tile;
      this.paused = reduceMotion.matches;
      this.current = -1;
      dialog.classList.remove('is-closing', 'is-pulling', 'is-releasing');
      dialog.showModal();
      document.documentElement.classList.add('uc-rv-open');
      this.track.scrollTop = index * this.track.clientHeight;
      this.setActive(index, tile);
      this.updateTiles();
      dialog.querySelector('[data-rv-close]')?.focus({ preventScroll: true });
      if (!this.hinted && this.slides.length > 1) {
        this.hinted = true;
        dialog.classList.add('is-hinting');
        setTimeout(() => dialog.classList.remove('is-hinting'), 3200);
      }
      track('reel_opened', { index, handle: this.slides[index]?.dataset.handle || '' });
      this.loop();
    }

    closeViewer({ restoreFocus = true } = {}) {
      const dialog = this.dialog;
      if (!dialog?.open || dialog.classList.contains('is-closing')) return;
      this.restoreFocus = restoreFocus;
      if (reduceMotion.matches) return dialog.close();
      dialog.classList.add('is-closing');
      setTimeout(() => dialog.open && dialog.close(), 220);
    }

    onViewerClosed() {
      const dialog = this.dialog;
      dialog.classList.remove('is-closing', 'is-pulling', 'is-releasing', 'is-hinting', 'is-paused');
      dialog.style.removeProperty('--pull');
      dialog.style.removeProperty('--pull-n');
      document.documentElement.classList.remove('uc-rv-open');
      cancelAnimationFrame(this.raf);
      this.slides.forEach((slide) => slide.querySelector('video')?.pause());
      const tile = this.tiles[this.current] || this.returnTile;
      this.current = -1;
      if (tile) {
        this.scrollRailTo(tile);
        if (this.restoreFocus !== false) tile.querySelector('[data-reel-open]')?.focus({ preventScroll: true });
      }
      this.restoreFocus = true;
      this.updateTiles();
    }

    go(index) {
      const i = Math.max(0, Math.min(this.slides.length - 1, index));
      if (i === this.current) return;
      this.track.scrollTo({ top: i * this.track.clientHeight, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    setActive(index, fromTile) {
      const slides = this.slides;
      const i = Math.max(0, Math.min(slides.length - 1, index));
      this.current = i;
      slides.forEach((slide, n) => {
        slide.classList.toggle('is-active', n === i);
        const video = slide.querySelector('video');
        if (n !== i && video && !video.paused) video.pause();
      });
      // huidige en volgende klaarzetten, rest niet
      const media = slides[i].querySelector('[data-rv-media]');
      const video = mountVideo(media);
      mountVideo(slides[i + 1]?.querySelector('[data-rv-media]'));
      if (video) {
        video.muted = this.muted;
        const tileVideo = fromTile?.querySelector('video');
        if (tileVideo && tileVideo.currentTime > 0 && video.readyState === 0) {
          video.addEventListener('loadedmetadata', () => { video.currentTime = tileVideo.currentTime; }, { once: true });
        } else if (video.readyState > 0 && !fromTile) {
          video.currentTime = 0;
        }
      }

      this.segs.forEach((seg, n) => {
        seg.classList.remove('is-image');
        seg.style.setProperty('--p', n < i ? '1' : '0');
      });
      const seg = this.segs[i];
      if (seg && !video) {
        void seg.offsetWidth;
        seg.classList.add('is-image');
      }

      if (this.countEl) this.countEl.textContent = this.strings.position.replace('%index%', i + 1).replace('%count%', slides.length);
      if (this.prevBtn) this.prevBtn.disabled = i === 0;
      if (this.nextBtn) this.nextBtn.disabled = i === slides.length - 1;
      this.applyPlayback();
    }

    applyPlayback() {
      const video = this.slides[this.current]?.querySelector('video');
      this.dialog.classList.toggle('is-paused', this.paused);
      this.playBtn?.setAttribute('aria-pressed', String(this.paused));
      this.playBtn?.setAttribute('aria-label', this.paused ? this.strings.play : this.strings.pause);
      if (!video) return;
      video.muted = this.muted;
      if (this.paused) video.pause();
      else safePlay(video);
    }

    setPaused(paused) {
      this.paused = paused;
      this.applyPlayback();
    }

    setMuted(muted) {
      this.muted = muted;
      this.soundBtn?.setAttribute('aria-pressed', String(!muted));
      this.soundBtn?.setAttribute('aria-label', muted ? this.strings.soundOn : this.strings.soundOff);
      const video = this.slides[this.current]?.querySelector('video');
      if (video) {
        video.muted = muted;
        if (!muted && !this.paused) safePlay(video);
      }
    }

    /** Voortgangsbalk van de actieve video */
    loop() {
      cancelAnimationFrame(this.raf);
      const step = () => {
        if (!this.dialog.open) return;
        const video = this.slides[this.current]?.querySelector('video');
        const seg = this.segs[this.current];
        if (video && seg && video.duration) seg.style.setProperty('--p', String(video.currentTime / video.duration));
        this.raf = requestAnimationFrame(step);
      };
      this.raf = requestAnimationFrame(step);
    }
  }

  customElements.define('uc-reels', UcReels);
})();
